import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import * as Bixby from '$/core';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import Loading from '$/components/loading/Loading';
import { ReactComponent as SendIcon } from '$/assets/icons/send.svg';
import { DataSiteService } from '$/services';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, ICellRendererParams } from 'ag-grid-enterprise';
import './index.scss';

interface IVerifyProps {
  setCurrentStage: (stage: Bixby.Enums.ClientDataSiteStage) => void;
  verificationResults: Bixby.Models.ClientDataSiteVerificationSummary[];
  isMnpiUpload: boolean;
}

interface ClientConfirmation {
  clientId: string;
  checked: boolean;
}

const Verify: React.FC<IVerifyProps> = props => {
  const { setCurrentStage, verificationResults, isMnpiUpload } = props;

  const [confirmModalActive, setConfirmModalActive] = useState(false as boolean);

  const [clientConfirmations, setClientConfirmations] = useState(
    verificationResults.map((r: Bixby.Models.ClientDataSiteVerificationSummary) => {
      return {
        clientId: r.clientId,
        checked: false,
      };
    }) as ClientConfirmation[]
  );
  const [updateReady, setUploadReady] = useState(false);

  const [uploadError, setUploadError] = useState(undefined as string | undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadConfirmed, setUploadConfirmed] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  const updateClientVerifications = (clientId: string) => {
    const client = clientConfirmations.find(x => x.clientId === clientId);
    if (!client) return;

    client.checked = !client.checked;

    if (clientConfirmations.every(x => x.checked)) {
      setUploadReady(true);
    } else {
      setUploadReady(false);
    }

    setClientConfirmations(clientConfirmations);
  };

  const handleUploadDataSites = async () => {
    setIsUploading(true);
    setUploadError(undefined);
    try {
      if (isMnpiUpload) {
        await DataSiteService.uploadMnpiDataSites(verificationResults);
      } else {
        await DataSiteService.uploadDataSites(verificationResults);
      }
      setConfirmModalActive(false);
      setIsUploading(false);
      setCurrentStage(Bixby.Enums.ClientDataSiteStage.Upload);
    } catch (e: any) {
      setIsUploading(false);
      setUploadError('Error uploading client data sites');
    }
  };

  const handleOpenConfirmUploadDataSites = () => {
    setConfirmModalActive(true);
  };

  const handleCancelUploadDataSites = () => {
    setConfirmModalActive(false);
  };

  const totalClientDataSites: Bixby.Models.ClientDataSite[] = [];
  for (let i = 0; i < (verificationResults || []).length; i++) {
    totalClientDataSites.push(...verificationResults[i].newDataSites);
  }

  const getAdditions: (clientName: string) => string[] = (clientName: string) => {
    let results: string[] = [];

    const client = (verificationResults || []).filter(x => x.clientName == clientName)[0];
    if (client) {
      results = (client.newDataSites || [])
        .map(vr => vr.dataSiteName)
        .filter(vr => !(client.currentDataSites || []).map(cds => cds.dataSiteName).includes(vr))
        .map(r => {
          return r;
        });
    }

    return results;
  };

  const getDeletions: (clientName: string) => string[] = (clientName: string) => {
    let results: string[] = [];

    const client = (verificationResults || []).filter(x => x.clientName == clientName)[0];
    if (client) {
      results = (client.currentDataSites || [])
        .map(vr => vr.dataSiteName)
        .filter(vr => !(client.newDataSites || []).map(cds => cds.dataSiteName).includes(vr))
        .map(r => {
          return r;
        });
    }

    return results;
  };

  const createClientSummaries: (
    verificationResults: Bixby.Models.ClientDataSiteVerificationSummary[]
  ) => JSX.Element[] = (verificationResults: Bixby.Models.ClientDataSiteVerificationSummary[]) => {
    return (verificationResults || []).map((result: Bixby.Models.ClientDataSiteVerificationSummary, index: number) => {
      const additions = getAdditions(result.clientName);
      const deletions = getDeletions(result.clientName);

      const clientId = result.clientId;

      return (
        <div className="verification_summary" key={index}>
          <BixCheckbox
            id={`checkbox-${clientId}`}
            onChange={() => {
              updateClientVerifications(clientId);
            }}>
            {result.clientName} (Current: {result.currentDataSites.length} / Proposed: {result.newDataSites.length})
          </BixCheckbox>
          <ul>
            <li>
              Additions
              <ul className="verification_changes">
                {!Bixby.Utils.arrayIsPopulated(additions) && <li>None</li>}
                {(additions || []).map((r, index) => {
                  return <li key={index}>{r}</li>;
                })}
              </ul>
            </li>
          </ul>
          <ul>
            <li>
              Deletions
              <ul className="verification_changes">
                {!Bixby.Utils.arrayIsPopulated(deletions) && <li>None</li>}
                {(deletions || []).map((r, index) => {
                  return <li key={index}>{r}</li>;
                })}
              </ul>
            </li>
          </ul>
        </div>
      );
    });
  };

  const companyIdCellRenderer = (params: ICellRendererParams) => {
    return Bixby.Utils.arrayIsPopulated(params.value) ? (
      <div
        className={classnames('company_links', {
          single_company: params.value.length === 1,
          multiple_companies: params.value.length > 1,
        })}>
        {params.value.map((row: any, index: number) => {
          return (
            <Link key={index} className="link_underline" to={`/admin/company/${row}`} target="_blank">
              Co {index + 1}
            </Link>
          );
        })}
      </div>
    ) : (
      <div></div>
    );
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  return (
    <React.Fragment>
      <section>
        <div className="client_datasites">
          <div className="top_wrap">
            <button
              type="button"
              className="nav_btn"
              onClick={() => setCurrentStage(Bixby.Enums.ClientDataSiteStage.Upload)}>
              Return to upload
            </button>
          </div>

          <div className="new_data_sites">
            <h3>{`Proposed Client Data Sites (${totalClientDataSites.length})`}</h3>
            {totalClientDataSites && !Bixby.Utils.isNullOrEmpty(totalClientDataSites) ? (
              <div className="proposed-datasites-data-grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      rowData={totalClientDataSites}
                      rowHeight={32}
                      headerHeight={32}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      onGridReady={onGridReady}
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                        lockPinned: true,
                        cellClass: 'align-left',
                      }}
                      components={{
                        companyIdCellRenderer: companyIdCellRenderer,
                      }}
                      columnDefs={[
                        {
                          headerName: 'Client',
                          field: 'clientName',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          minWidth: 100,
                          cellRenderer: (params: ICellRendererParams) => {
                            return (
                              <a
                                className="bix-link link_underline"
                                target="_blank"
                                href={`/admin/client/${params.data['clientId']}`}>
                                {params.value}
                              </a>
                            );
                          },
                        },
                        {
                          headerName: 'Data Site Name',
                          field: 'dataSiteName',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          minWidth: 250,
                          flex: 1,
                        },
                        {
                          field: 'companyExistsInLibrary',
                          headerName: 'Library?',
                          minWidth: 120,
                          width: 120,
                          valueFormatter: (params: any) => {
                            return params.data['companyExistsInLibrary'] ? 'Yes' : 'No';
                          },
                        },
                        {
                          field: 'clientHasAccess',
                          headerName: 'Client?',
                          minWidth: 120,
                          width: 120,
                          valueFormatter: (params: any) => {
                            return params.data['clientHasAccess'] ? 'Yes' : 'No';
                          },
                        },
                        {
                          headerName: 'Companies',
                          field: 'companyIds',
                          cellRenderer: 'companyIdCellRenderer',
                          minWidth: 150,
                          width: 150,
                        },
                        {
                          headerName: 'Declaration',
                          field: 'declaration',
                          minWidth: 150,
                          width: 150,
                          hide: !isMnpiUpload,
                        },
                        {
                          headerName: 'Status',
                          field: 'notes',
                          filter: 'agTextColumnFilter',
                          floatingFilter: true,
                          minWidth: 250,
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>
            ) : (
              <p>{`There are no verification results found`}</p>
            )}
            {createClientSummaries(verificationResults)}
            {totalClientDataSites && !Bixby.Utils.isNullOrEmpty(totalClientDataSites) && (
              <button
                type="button"
                className="upload_btn"
                disabled={!updateReady}
                onClick={() => handleOpenConfirmUploadDataSites()}>
                Upload new results
                <div className="icon_wrap">
                  <SendIcon />
                </div>
              </button>
            )}
          </div>
        </div>
        <Modal
          title={`Upload Proposed Client Data Sites (${(verificationResults || []).length})?`}
          active={confirmModalActive}
          onClose={handleCancelUploadDataSites}>
          <div className="button_side_wrap">
            {isUploading && <Loading inline />}
            {!isUploading && (
              <div className="modal_upload_buttons">
                <BixCheckbox id="relatedText" onChange={() => setUploadConfirmed(!uploadConfirmed)}>
                  Confirm upload
                </BixCheckbox>
                <BixButton type="button" disabled={!uploadConfirmed} onClick={handleUploadDataSites}>
                  Upload
                </BixButton>
                <BixButton light type="button" onClick={handleCancelUploadDataSites}>
                  Cancel
                </BixButton>
              </div>
            )}
            {uploadError && <p className="error">{uploadError}</p>}
          </div>
        </Modal>
      </section>
    </React.Fragment>
  );
};

export default Verify;
